import { pdfjs } from "react-pdf";
import React, { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  ArrowUpTrayIcon,
  ClipboardDocumentListIcon,
  PaperClipIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { getOfferAsync, updateOfferAsync } from "../../../slices/offer";
import { DocumentManagerV6 } from "../Media/DocumentManagerV6";
import { DocumentUploadSectionV5 } from "../Media/DocumentUploadSectionV5";
import { ARCHIVE_ACCEPT } from "../projects/folders/archive-utils";
import "./offer-workspace.css";
import {
  analyzeOfferDocument,
  analyzeOfferDocumentFallback,
} from "../../../api/offer";
import { isOfferFeatureEnabled } from "./utils";
import { newClientKey } from "./utils/offer-workspace";
import { flushOfferSaves } from "./utils/offer-save-queue";

function OfferDocumentAiPanel({ offer, documents }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [documentId, setDocumentId] = useState("");
  const [documentText, setDocumentText] = useState("");
  const [extracting, setExtracting] = useState(false);
  const extractionEpoch = useRef(0);
  const extractPdf = async () => {
    const source = documents.find((document) => String(document.id) === String(documentId));
    const url = source?.file?.url;
    if (!url) { toast.error(t("offers.workspace.extract_failed")); return; }
    const epoch = ++extractionEpoch.current;
    setExtracting(true);
    let pdf;
    try {
      pdf = await pdfjs.getDocument({ url }).promise;
      const pages = [];
      for (let page = 1; page <= pdf.numPages; page += 1) {
        if (epoch !== extractionEpoch.current) return;
        const content = await (await pdf.getPage(page)).getTextContent();
        pages.push(`[Side ${page}]\n${content.items.map((item) => item.str || "").join(" ")}`);
      }
      if (!pages.some((page) => page.replace(/\[Side \d+\]/, "").trim())) throw new Error("no_text");
      if (epoch === extractionEpoch.current) setDocumentText(pages.join("\n\n"));
    } catch { toast.error(t("offers.workspace.extract_failed")); }
    finally { await pdf?.destroy(); if (epoch === extractionEpoch.current) setExtracting(false); }
  };
  const [result, setResult] = useState(null);
  const [selectedLines, setSelectedLines] = useState([]);
  const [loading, setLoading] = useState(false);

  const formatSuggestionLine = (line) => {
    if (line == null) return "";
    if (typeof line === "string") return line;
    const title = line.title || line.name || "";
    const description = line.description || "";
    const qty = line.quantity != null ? String(line.quantity) : "";
    const unit = line.unit || "";
    const qtyUnit = [qty, unit].filter(Boolean).join(" ");
    return [title, qtyUnit, description, line.source_reference].filter(Boolean).join(" — ");
  };

  const normalizeSuggestions = (response) => {
    const nested =
      response?.analysis ||
      response?.generated_fields?.analysis ||
      response?.suggestions ||
      response?.data?.analysis ||
      response?.data?.suggestions ||
      response?.data ||
      response;
    if (typeof nested === "string") {
      try {
        return JSON.parse(nested);
      } catch {
        return { summary: nested };
      }
    }
    return nested && typeof nested === "object" ? nested : {};
  };

  const analyze = async () => {
    if (!documentText.trim()) {
      toast.error(t("offers.documentAi.source_required"));
      return;
    }
    setLoading(true);
    const payload = {
      ...(documentId ? { document_id: documentId } : {}),
      ...(documentText.trim() ? { document_text: documentText.trim() } : {}),
    };
    let response = await analyzeOfferDocument(offer.id, payload);
    if (response?.status === 404) {
      response = await analyzeOfferDocumentFallback(offer.id, {
        document_text:
          documentText.trim() ||
          `Document id ${documentId || "unknown"}`,
        offer_title: offer?.displayTitle || offer?.offer_number,
        customer_name: offer?.customer?.name,
        project_name: offer?.project?.name,
        reference: offer?.reference,
        comment: offer?.comment,
      });
    }
    setLoading(false);
    if (response?.error || response?.errors) {
      toast.error(t("offers.documentAi.failed"));
      return;
    }
    const suggestions = normalizeSuggestions(response);
    setResult(suggestions || {});
    setSelectedLines([]);
  };

  const applySelectedLines = async () => {
    const lines = (result?.suggested_lines || []).filter((_, index) =>
      selectedLines.includes(index)
    );
    if (!lines.length) return;

    const formatted = lines.map(formatSuggestionLine).filter(Boolean);
    const appended = [offer.comment, ...formatted].filter(Boolean).join("\n");
    const action = await dispatch(
      updateOfferAsync({ id: offer.id, comment: appended })
    );
    if (updateOfferAsync.rejected.match(action)) {
      toast.error(t("offers.documentAi.apply_failed"));
      return;
    }
    dispatch(getOfferAsync(offer.id));
    toast.success(t("offers.documentAi.applied"));
  };

  const applyAsOfferLines = async () => {
    const selected = (result?.suggested_lines || []).filter((suggestion, index) => suggestion && selectedLines.includes(index));
    if (!selected.length) return;
    try {
      await flushOfferSaves(offer.id);
    } catch {
      toast.error(t("offers.workspace.save_error"));
      return;
    }
    const latest = await dispatch(getOfferAsync(offer.id));
    const fresh = latest.payload?.offer || latest.payload || offer;
    const existing = fresh.items || [];
    const added = selected.map((suggestion, index) => ({
      client_key: newClientKey(), item_type: "manual", title: typeof suggestion === "string" ? suggestion : suggestion.title || suggestion.name || "",
      description: typeof suggestion === "string" ? "" : [suggestion.description, suggestion.source_reference].filter(Boolean).join("\n"),
      quantity: typeof suggestion === "object" && Number.isFinite(Number(suggestion.quantity)) && Number(suggestion.quantity) > 0 ? Number(suggestion.quantity) : 1,
      sales_price: 0, discount: 0, surcharge: 0, pricing_role: "included", visible_to_customer: true,
      row_order: existing.length + index, price_source: "document_analysis"
    }));
    const response = await dispatch(updateOfferAsync({ id: offer.id, items: [...existing, ...added], items_version: fresh.items_version }));
    if (updateOfferAsync.rejected.match(response)) { toast.error(t("offers.workspace.save_error")); return; }
    setSelectedLines([]);
    toast.success(t("offers.workspace.suggestions_added"));
  };

  const copySelectedLines = async () => {
    const lines = (result?.suggested_lines || []).filter((_, index) =>
      selectedLines.includes(index)
    );
    if (!lines.length) return;
    try {
      await navigator.clipboard.writeText(
        lines.map(formatSuggestionLine).filter(Boolean).join("\n")
      );
      toast.success(t("offers.documentAi.copied"));
    } catch {
      toast.error(t("offers.documentAi.copy_failed"));
    }
  };

  const suggestedLines = Array.isArray(result?.suggested_lines)
    ? result.suggested_lines
    : [];

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <p className="mb-4 rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{t("offers.workspace.ai_source")}</p>
      {selectedLines.length > 0 && <button type="button" className="mb-4 rounded-lg bg-blue-700 px-4 py-2 text-sm text-white" onClick={applyAsOfferLines}>{t("offers.workspace.add_suggestions")}</button>}
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-[#EAF3FD] p-2 text-[#1463C7]">
          <SparklesIcon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">
            {t("offers.documentAi.title")}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {t("offers.documentAi.intro")}
          </p>
        </div>
      </div>
      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            {t("offers.documentAi.document")}
          </label>
          <select
            value={documentId}
            onChange={(event) => { extractionEpoch.current += 1; setExtracting(false); setDocumentText(""); setDocumentId(event.target.value); }}
            className="offer-select"
          >
            <option value="">{t("offers.documentAi.select_document")}</option>
            {documents.map((document) => (
              <option key={document.id} value={document.id}>
                {document.title || document.name || document.filename}
              </option>
            ))}
          </select>
          <button type="button" className="mt-2 rounded-lg border border-blue-200 px-3 py-2 text-sm text-blue-800" disabled={!documentId || extracting} onClick={extractPdf}>{t(extracting ? "offers.workspace.extracting" : "offers.workspace.extract")}</button>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            {t("offers.documentAi.paste_text")}
          </label>
          <textarea
            value={documentText}
            onChange={(event) => setDocumentText(event.target.value)}
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
      </div>
      <button
        type="button"
        onClick={analyze}
        disabled={loading}
        className="mt-4 rounded-lg bg-[#1463C7] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0B3A78] disabled:opacity-50"
      >
        {loading ? t("offers.documentAi.analyzing") : t("offers.documentAi.analyze")}
      </button>

      {result && (
        <div className="mt-6 space-y-4 border-t border-slate-200 pt-5">
          {result.summary && (
            <SuggestionSection title={t("offers.documentAi.summary")}>
              <p className="text-sm text-slate-700">{result.summary}</p>
            </SuggestionSection>
          )}
          <SuggestionSection title={t("offers.documentAi.gaps")}>
            <SuggestionList values={result.gaps} />
          </SuggestionSection>
          <SuggestionSection title={t("offers.documentAi.checklist_hints")}>
            <SuggestionList values={result.checklist_hints} />
          </SuggestionSection>
          {suggestedLines.length > 0 && (
            <SuggestionSection title={t("offers.documentAi.suggested_lines")}>
              <div className="space-y-2">
                {suggestedLines.map((line, index) => (
                  <label
                    key={`suggested-line-${index}`}
                    className="flex cursor-pointer gap-2 rounded-lg border border-slate-200 p-3 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedLines.includes(index)}
                      onChange={() =>
                        setSelectedLines((current) =>
                          current.includes(index)
                            ? current.filter((item) => item !== index)
                            : [...current, index]
                        )
                      }
                    />
                    <span>{formatSuggestionLine(line)}</span>
                  </label>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={copySelectedLines}
                  disabled={!selectedLines.length}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
                >
                  {t("offers.documentAi.copy")}
                </button>
                <button
                  type="button"
                  onClick={applySelectedLines}
                  disabled={!selectedLines.length}
                  className="rounded-lg bg-[#1463C7] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {t("offers.documentAi.apply_notes")}
                </button>
              </div>
            </SuggestionSection>
          )}
        </div>
      )}
    </section>
  );
}

function SuggestionSection({ title, children }) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function SuggestionList({ values }) {
  if (!Array.isArray(values) || values.length === 0) return null;
  return (
    <ul className="list-inside list-disc space-y-1 text-sm text-slate-700">
      {values.map((value, index) => {
        const label =
          typeof value === "string"
            ? value
            : [value?.title, value?.quantity, value?.description]
                .filter(Boolean)
                .join(" — ");
        return <li key={`suggestion-${index}`}>{label}</li>;
      })}
    </ul>
  );
}

/**
 * Vedlegg tab — same upload UX as project document archive
 * (DocumentUploadSectionV5 compact dialog), plus grid edit/delete.
 */
export default function OfferAttachmentsTab({ offer }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [outgoingSaving, setOutgoingSaving] = useState(false);
  const [uploadRevision, setUploadRevision] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const uploadPickerRef = useRef(null);
  const ingestFilesRef = useRef(null);
  const dragDepthRef = useRef(0);

  const saveOutgoing = async (documentId, checked) => {
    const ids = Array.isArray(offer.outgoing_document_ids) ? offer.outgoing_document_ids : [];
    const next = checked ? [...new Set([...ids, documentId])] : ids.filter((id) => String(id) !== String(documentId));
    setOutgoingSaving(true);
    const result = await dispatch(updateOfferAsync({ id: offer.id, outgoing_document_ids: next }));
    if (updateOfferAsync.rejected.match(result)) toast.error(t("offers.workspace.save_error"));
    setOutgoingSaving(false);
  };

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    if (Array.from(event.dataTransfer?.types || []).includes("Files")) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);
    const files = event.dataTransfer?.files;
    if (files?.length) ingestFilesRef.current?.(files);
  }, []);

  const aiEnabled = isOfferFeatureEnabled(
    offer?.offer_feature_flags || {},
    "offer_document_ai"
  );

  if (!offer?.id) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
        <PaperClipIcon className="h-8 w-8 text-slate-300" aria-hidden="true" />
        <p className="text-sm font-medium text-slate-700">
          {t("offers.tabs.attachments")}
        </p>
        <p className="max-w-md text-sm text-slate-500">
          {t("offers.tabs.no_offer")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t("offers.tabs.attachments")}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t("offers.tabs.attachments_intro")}
        </p>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-slate-100 p-2 text-slate-600">
              <ClipboardDocumentListIcon className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">
                {t("offers.documentAi.attachments_title")}
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                {t("offers.documentAi.attachments_intro")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => uploadPickerRef.current?.()}
            className="flex h-[42px] shrink-0 items-center justify-center gap-2 rounded-[20px] bg-[#0F766E] px-4 text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(15,118,110,0.7)] hover:bg-[#0B5A54]"
          >
            <span className="text-lg leading-none">+</span>
            {t("document_archive.upload")}
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <div
            role="button"
            tabIndex={0}
            onClick={() => uploadPickerRef.current?.()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                uploadPickerRef.current?.();
              }
            }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[20px] border-2 border-dashed px-4 py-10 text-center transition-colors ${
              isDragging
                ? "border-[#0F766E] bg-[#E6F2F0]"
                : "border-[#D5DEDB] bg-[#FBFCFC] hover:border-[#0F766E] hover:bg-[#F3F8F7]"
            }`}
          >
            <ArrowUpTrayIcon
              className={`h-8 w-8 ${isDragging ? "text-[#0F766E]" : "text-[#8B9895]"}`}
              aria-hidden="true"
            />
            <p className="text-sm font-semibold text-[#10221F]">
              {isDragging
                ? t("offers.attachments.drop_here")
                : t("offers.attachments.dropzone_title")}
            </p>
            <p className="text-xs text-[#8B9895]">
              {t("offers.attachments.dropzone_hint")}
            </p>
          </div>

          {/* Same compact uploader dialog as /dashboard/projects/:id/archive */}
          <div className="sr-only" aria-hidden>
            <DocumentUploadSectionV5
              key={`offer-upload-${offer.id}`}
              hideTrigger
              openPickerRef={uploadPickerRef}
              ingestFilesRef={ingestFilesRef}
              multiple
              module="offer"
              moduleParam={String(offer.id)}
              allowedFileTypes={ARCHIVE_ACCEPT}
              layout="compact"
              onDocumentUploaded={() => setUploadRevision((n) => n + 1)}
            />
          </div>

          <DocumentManagerV6
            key={`offer-attachments-${offer.id}-${uploadRevision}`}
            module="offer"
            moduleParam={String(offer.id)}
            allowMultiple
            mode="grid"
            showUpload={false}
            showExisting
            allowDelete
            allowEdit
            autoLoad
            syncDocumentsToParent
            onDocumentsChange={(nextDocuments) =>
              setDocuments(Array.isArray(nextDocuments) ? nextDocuments : [])
            }
          />
        </div>
      </section>

      <section className="rounded-xl border border-blue-200 bg-blue-50 p-5"><h3 className="font-semibold text-blue-950">{t("offers.workspace.outgoing")}</h3><p className="my-2 text-sm text-blue-900">{t("offers.workspace.outgoing_hint")}</p>
        {documents.map((document) => <label key={document.id} className="flex items-center gap-3 py-2 text-sm"><input type="checkbox" disabled={outgoingSaving} checked={(offer.outgoing_document_ids || []).some((id) => String(id) === String(document.id))} onChange={(event) => saveOutgoing(document.id, event.target.checked)} />{document.title || document.document_file_name}</label>)}
      </section>
      {aiEnabled && <OfferDocumentAiPanel offer={offer} documents={documents} />}
    </div>
  );
}
